import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES, formatBandwidth } from "@/lib/format";

const empty = {
  name: "", description: "", price_kes: 3000,
  upload_kbps: 5120, download_kbps: 10240, billing_period_days: 30, active: true,
};

export default function PppoePlans() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pppoe/plans");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name, description: p.description || "",
      price_kes: p.price_kes, upload_kbps: p.upload_kbps, download_kbps: p.download_kbps,
      billing_period_days: p.billing_period_days, active: p.active,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) await api.put(`/admin/pppoe/plans/${editing}`, form);
      else await api.post("/admin/pppoe/plans", form);
      toast.success(editing ? "Plan updated" : "Plan created");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this plan?")) return;
    try {
      await api.delete(`/admin/pppoe/plans/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <PageHeader
        testId="pppoe-plans-header"
        title="PPPoE Plans"
        subtitle="Recurring wired internet plans, billed on a rolling cycle"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-pppoe-plan-btn"
                onClick={openNew}
                className="h-10 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold"
              >
                <Plus className="h-4 w-4 mr-1" /> NEW PLAN
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? "Edit Plan" : "New Plan"}</DialogTitle>
                <DialogDescription className="text-nhp-muted">
                  Configure the plan name, monthly price, and bandwidth limits.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none min-h-[60px]" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Price (KES)</Label>
                    <Input type="number" value={form.price_kes} onChange={(e) => setForm({ ...form, price_kes: +e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Billing period (days)</Label>
                    <Input type="number" min="1" value={form.billing_period_days} onChange={(e) => setForm({ ...form, billing_period_days: Math.max(1, +e.target.value) })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Upload (Mbps)</Label>
                    <Input type="number" step="0.1" value={form.upload_kbps / 1024} onChange={(e) => setForm({ ...form, upload_kbps: Math.round(+e.target.value * 1024) })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Download (Mbps)</Label>
                    <Input type="number" step="0.1" value={form.download_kbps / 1024} onChange={(e) => setForm({ ...form, download_kbps: Math.round(+e.target.value * 1024) })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                  </div>
                </div>
                <div className="flex items-center justify-between border border-nhp-border px-3 py-2">
                  <Label className="text-sm">Active (available for new accounts)</Label>
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={submit} className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
                  SAVE
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-4 md:p-8">
        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Billing cycle</div>
            <div className="col-span-2">Bandwidth</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-nhp-border">
            {items.length === 0 && (
              <div className="p-8 text-center text-sm text-nhp-muted">No PPPoE plans yet.</div>
            )}
            {items.map((p) => (
              <div key={p.id} className="flex flex-col gap-2 md:grid md:grid-cols-12 px-4 md:px-6 py-4 md:items-center hover:bg-nhp-bg">
                <div className="flex items-start justify-between gap-3 md:contents">
                  <div className="md:col-span-3">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-nhp-muted line-clamp-1">{p.description}</div>
                  </div>
                  <div className="flex items-center gap-2 md:col-span-2 md:justify-end md:order-6">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)} className="h-8 rounded-none text-nhp-muted hover:text-nhp-text">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="h-8 rounded-none text-nhp-muted hover:text-nhp-critical">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm md:contents">
                  <div className="font-mono md:col-span-2 text-nhp-muted">Every {p.billing_period_days}d</div>
                  <div className="font-mono text-nhp-muted md:col-span-2">
                    ↓ {formatBandwidth(p.download_kbps)} · ↑ {formatBandwidth(p.upload_kbps)}
                  </div>
                  <div className="font-mono font-bold text-nhp-primary tabular-nums md:col-span-2">{KES(p.price_kes)}</div>
                  <div className="md:col-span-1">
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${p.active ? "text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10" : "text-nhp-muted border-nhp-border"}`}>
                      {p.active ? "LIVE" : "OFF"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
